import apiClient from './api';

export interface PartnerRegistrationData {
  name: string;
  email: string;
  phone: string;
  password: string;
  business_name: string;
  business_address: string;
  gstin: string;
  pan: string;
  gym_name: string;
  gym_address: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  total_capacity: number;
}

export interface PartnerLoginData {
  email: string;
  password: string;
}

export interface Partner {
  id: number;
  name: string;
  email: string;
  phone: string;
  user_type: string;
  is_partner_verified: boolean;
  business_name: string;
  business_address: string;
  gstin: string;
  pan: string;
  bank_account_number?: string;
  bank_ifsc?: string;
  bank_account_holder_name?: string;
  created_at: string;
  gyms?: Gym[];
}

export interface Gym {
  id: number;
  name: string;
  city: string;
  is_active: boolean;
  is_verified: boolean;
  rating: number;
  total_reviews: number;
  total_slots?: number;
  active_bookings?: number;
  created_at: string;
}

export interface AuthResponse {
  partnerId: number;
  partner?: Partner;
  token: string;
  refreshToken: string;
  expiresIn?: number;
  message: string;
}

export interface PartnerAnalytics {
  total_gyms: number;
  total_bookings: number;
  completed_bookings: number;
  cancelled_bookings: number;
  total_revenue: number;
  coins_revenue: number;
  cash_revenue: number;
  unique_users: number;
  average_rating: number;
}

class PartnerService {
  /**
   * Register new partner
   */
  async registerPartner(data: PartnerRegistrationData): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/partners/register', data);
  }

  /**
   * Send login OTP to phone
   */
  async sendLoginOTP(phone: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      '/partners/send-login-otp',
      { phone }
    );
  }

  /**
   * Verify login OTP
   */
  async verifyLoginOTP(credentials: { phone: string; otp: string }): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/partners/verify-login-otp', credentials);
  }

  /**
   * Partner login
   */
  async loginPartner(data: PartnerLoginData): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/partners/login', data);
  }

  /**
   * Get partner profile
   */
  async getPartnerProfile(): Promise<Partner> {
    return apiClient.get<Partner>('/partners/profile');
  }

  /**
   * Update partner profile
   */
  async updatePartnerProfile(data: Partial<Partner>): Promise<{ message: string; profile: Partner }> {
    return apiClient.put<{ message: string; profile: Partner }>('/partners/profile', data);
  }

  /**
   * Verify partner OTP
   */
  async verifyPartnerOTP(otp: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/partners/verify-otp', { otp });
  }

  /**
   * Resend OTP to partner
   */
  async resendPartnerOTP(): Promise<{ message: string; phone: string }> {
    return apiClient.post<{ message: string; phone: string }>('/partners/resend-otp');
  }

  /**
   * Get partner analytics
   */
  async getPartnerAnalytics(startDate?: string, endDate?: string): Promise<PartnerAnalytics> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return apiClient.get<PartnerAnalytics>(
      `/partners/analytics${params.toString() ? '?' + params.toString() : ''}`
    );
  }

  /**
   * Logout partner
   */
  logout(): void {
    apiClient.clearToken();
    localStorage.removeItem('partner');
  }
}

const partnerService = new PartnerService();
export default partnerService;
